import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, BookText, User, Scale, Plus, X, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

export default function AIExpert() {
  const { user } = useAuth();
  const isStaff = ["admin", "staff"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [decisionsCount, setDecisionsCount] = useState(0);
  const sessionId = useRef(crypto.randomUUID());
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/library/meta").then((r) => { setMeta(r.data); setState(r.data.default_state); }).catch(() => {});
    api.get("/expert/decisions").then((r) => setDecisionsCount(r.data.decisions.length)).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const ask = async (text) => {
    if (!text.trim() || busy) return;
    setError("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/expert/chat", { session_id: sessionId.current, message: text, state });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, citations: r.data.citations }]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const runQuickWin = async (qw) => {
    if (busy) return;
    setError("");
    setMessages((m) => [...m, { role: "user", content: qw.label }]);
    setBusy(true);
    try {
      const r = await api.post("/expert/quickwin", { id: qw.id, state });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, citations: r.data.citations }]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="text-white flex flex-col h-[calc(100vh-9rem)]" data-testid="expert-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Sheet 09 — AI Expert</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Grounded recovery expert</h1>
          <p className="text-white/55 mt-2 max-w-2xl font-light">Learns from everything in the Document Library plus your recorded assessor decisions — with citations to the source.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="expert-state-selector">
          <button onClick={() => setDecisionsOpen(true)} data-testid="expert-decisions-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 px-3 py-1.5 rounded-sm transition-colors mr-2">
            <Scale className="h-3.5 w-3.5" /> Decisions &amp; outcomes ({decisionsCount})
          </button>
          {meta?.states?.map((s) => (
            <button key={s} onClick={() => setState(s)} data-testid={`expert-state-${s}`}
              className={`font-mono text-[0.6rem] tracking-widest px-2.5 py-1 rounded-sm border transition-colors ${state === s ? "bg-gold-500 text-navy-950 border-gold-500 font-bold" : "border-gold-500/25 text-white/55 hover:text-white"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* quick wins */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="expert-quickwins">
        {meta?.quickwins?.map((qw) => (
          <button key={qw.id} onClick={() => runQuickWin(qw)} disabled={busy} data-testid={`quickwin-${qw.id}`}
            className="inline-flex items-center gap-1.5 text-xs border border-gold-500/25 bg-navy-900/40 text-white/75 hover:border-gold-500/50 hover:text-white px-3 py-2 rounded-sm transition-colors disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5 text-gold-400" /> {qw.label}
          </button>
        ))}
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto border border-gold-500/15 bg-navy-950/60 rounded-sm p-5 space-y-5" data-testid="expert-messages">
        {messages.length === 0 && (
          <div className="text-white/35 text-sm h-full flex flex-col items-center justify-center gap-3">
            <BookText className="h-8 w-8 text-gold-500/40" />
            Ask a question about DRFA / NDP claims, or run a quick win above.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}`}>
            {m.role === "assistant" && <div className="h-8 w-8 shrink-0 rounded-sm bg-gold-500 text-navy-950 flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>}
            <div className={`max-w-[80%] rounded-sm px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-gold-500 text-navy-950 font-medium" : "bg-navy-900 border border-gold-500/15 text-zinc-200"}`}>
              <div className="whitespace-pre-wrap">{m.content}</div>
              {m.citations?.length > 0 && (
                <div className="mt-3 pt-2 border-t border-white/10 flex flex-wrap gap-1.5">
                  {m.citations.map((c) => (
                    <span key={c} className="font-mono text-[0.5rem] uppercase tracking-widest text-gold-400/80 border border-gold-500/25 px-1.5 py-0.5 rounded-sm">{c}</span>
                  ))}
                </div>
              )}
            </div>
            {m.role === "user" && <div className="h-8 w-8 shrink-0 rounded-sm bg-navy-800 flex items-center justify-center"><User className="h-4 w-4 text-white/60" /></div>}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-white/40 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Consulting the library…</div>}
        <div ref={endRef} />
      </div>

      {error && <div className="mt-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-2 rounded-sm">{error}</div>}

      {/* input */}
      <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="mt-4 flex items-center gap-3" data-testid="expert-input-form">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the AI Expert…" data-testid="expert-input"
          className="flex-1 bg-navy-900 border border-gold-500/25 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-gold-500/60" />
        <button type="submit" disabled={busy || !input.trim()} data-testid="expert-send" className="btn-orb-plate px-5 py-3 rounded-md disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </form>

      {decisionsOpen && (
        <DecisionsPanel isStaff={isStaff} onClose={() => {
          setDecisionsOpen(false);
          api.get("/expert/decisions").then((r) => setDecisionsCount(r.data.decisions.length)).catch(() => {});
        }} />
      )}
    </div>
  );
}

const OUTCOME_CHIP = {
  approved: "border-emerald-400/50 text-emerald-300",
  partially_approved: "border-gold-500/50 text-gold-300",
  rejected: "border-red-400/50 text-red-300",
  conditional: "border-sky-400/50 text-sky-300",
  pending_review: "border-white/25 text-white/50",
};

const dinp = "w-full bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60";
const dlbl = "font-mono uppercase tracking-[0.18em] text-white/50 text-[0.6rem] block mb-1";

function DecisionsPanel({ isStaff, onClose }) {
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    title: "", authority: "NSWRA", decision: "approved", agrn: "", event_name: "",
    decided_on: "", amount_claimed: "", amount_approved: "", outcome_summary: "", rationale: "",
  });

  const load = () => api.get("/expert/decisions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/expert/decisions", {
        ...form,
        amount_claimed: form.amount_claimed === "" ? null : Number(form.amount_claimed),
        amount_approved: form.amount_approved === "" ? null : Number(form.amount_approved),
        agrn: form.agrn || null, event_name: form.event_name || null, decided_on: form.decided_on || null,
        outcome_summary: form.outcome_summary || null, rationale: form.rationale || null,
      });
      setFormOpen(false);
      setForm((f) => ({ ...f, title: "", agrn: "", event_name: "", decided_on: "", amount_claimed: "", amount_approved: "", outcome_summary: "", rationale: "" }));
      load();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this decision from the Expert's knowledge?")) return;
    await api.delete(`/expert/decisions/${id}`);
    load();
  };

  const toggleLearn = async (d) => {
    await api.patch(`/expert/decisions/${d.id}`, { learn_enabled: d.learn_enabled === false });
    load();
  };

  const decisions = data?.decisions || [];

  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex justify-end" onClick={() => onClose(decisions.length)}>
      <div onClick={(e) => e.stopPropagation()} data-testid="decisions-panel"
        className="w-full max-w-xl h-full bg-navy-900 border-l border-gold-500/25 flex flex-col text-white">
        <div className="px-6 h-20 flex items-center justify-between border-b border-gold-500/20">
          <div>
            <Mono className="text-gold-400 text-[0.55rem]">Admin-controlled knowledge</Mono>
            <div className="font-heading text-xl flex items-center gap-2 mt-0.5"><Scale className="h-5 w-5 text-gold-400" /> Decisions &amp; outcomes</div>
          </div>
          <button onClick={() => onClose(decisions.length)} data-testid="decisions-close" className="text-white/60 p-2"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-4 border-b border-gold-500/15 text-xs text-white/50 font-light">
          Record NSWRA / QRA / EMV determinations here — approvals, reductions, rejections and the assessor's reasoning.
          The AI Expert treats these as precedent and cites them in answers.
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {isStaff && (
            <button onClick={() => setFormOpen((v) => !v)} data-testid="decision-add-btn"
              className="w-full border border-dashed border-gold-500/40 text-gold-300 hover:bg-gold-500/10 py-2.5 rounded-sm text-sm inline-flex items-center justify-center gap-2 transition-colors">
              <Plus className="h-4 w-4" /> Record a decision
            </button>
          )}

          {formOpen && (
            <form onSubmit={save} className="border border-gold-500/25 bg-navy-950/60 rounded-sm p-4 space-y-3" data-testid="decision-form">
              <div>
                <label className={dlbl}>Title *</label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required data-testid="decision-title" className={dinp}
                  placeholder="e.g. Gravel resheeting claim reduced — insufficient pre-disaster evidence" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={dlbl}>Authority</label>
                  <select value={form.authority} onChange={(e) => setForm((f) => ({ ...f, authority: e.target.value }))} data-testid="decision-authority" className={dinp}>
                    {data?.authorities?.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className={dlbl}>Outcome</label>
                  <select value={form.decision} onChange={(e) => setForm((f) => ({ ...f, decision: e.target.value }))} data-testid="decision-outcome" className={dinp}>
                    {data?.outcomes?.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className={dlbl}>Decided on</label>
                  <input type="date" value={form.decided_on} onChange={(e) => setForm((f) => ({ ...f, decided_on: e.target.value }))} data-testid="decision-date" className={dinp} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={dlbl}>AGRN</label><input value={form.agrn} onChange={(e) => setForm((f) => ({ ...f, agrn: e.target.value }))} className={dinp} data-testid="decision-agrn" /></div>
                <div><label className={dlbl}>Event</label><input value={form.event_name} onChange={(e) => setForm((f) => ({ ...f, event_name: e.target.value }))} className={dinp} data-testid="decision-event" /></div>
                <div><label className={dlbl}>$ claimed</label><input type="number" step="0.01" value={form.amount_claimed} onChange={(e) => setForm((f) => ({ ...f, amount_claimed: e.target.value }))} className={dinp} data-testid="decision-claimed" /></div>
                <div><label className={dlbl}>$ approved</label><input type="number" step="0.01" value={form.amount_approved} onChange={(e) => setForm((f) => ({ ...f, amount_approved: e.target.value }))} className={dinp} data-testid="decision-approved" /></div>
              </div>
              <div>
                <label className={dlbl}>Outcome summary</label>
                <textarea rows={2} value={form.outcome_summary} onChange={(e) => setForm((f) => ({ ...f, outcome_summary: e.target.value }))} className={dinp} data-testid="decision-summary" />
              </div>
              <div>
                <label className={dlbl}>Assessor rationale / lesson learned</label>
                <textarea rows={3} value={form.rationale} onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value }))} className={dinp} data-testid="decision-rationale"
                  placeholder="Why was it decided this way? What should we do differently next claim?" />
              </div>
              {err && <div className="text-red-300 text-xs">{err}</div>}
              <button type="submit" disabled={busy} data-testid="decision-save"
                className="w-full bg-gold-500 text-navy-950 font-semibold py-2.5 rounded-sm hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save decision
              </button>
            </form>
          )}

          {data === null ? (
            <div className="text-white/40 text-sm">Loading…</div>
          ) : decisions.length === 0 && !formOpen ? (
            <div className="text-white/40 text-sm text-center py-10" data-testid="decisions-empty">
              No decisions recorded yet{isStaff ? " — record the first NSWRA determination above." : "."}
            </div>
          ) : (
            decisions.map((d) => (
              <div key={d.id} data-testid={`decision-${d.id}`}
                className={`border border-gold-500/20 bg-navy-950/50 rounded-sm p-4 ${d.learn_enabled === false ? "opacity-50" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mono className="text-[0.5rem] text-gold-400/80">{d.authority}</Mono>
                      <span className={`font-mono uppercase tracking-widest text-[0.5rem] border px-1.5 py-0.5 rounded-sm ${OUTCOME_CHIP[d.decision] || OUTCOME_CHIP.pending_review}`}>
                        {(d.decision || "").replace("_", " ")}
                      </span>
                      {d.decided_on && <span className="text-white/35 text-[0.6rem] font-mono">{d.decided_on}</span>}
                    </div>
                    <div className="font-heading text-sm mt-1.5 leading-snug">{d.title}</div>
                    {(d.amount_claimed != null || d.amount_approved != null) && (
                      <div className="text-[0.65rem] font-mono text-white/50 mt-1">
                        {d.amount_claimed != null && <>claimed ${Number(d.amount_claimed).toLocaleString()}</>}
                        {d.amount_approved != null && <span className="text-emerald-300"> → approved ${Number(d.amount_approved).toLocaleString()}</span>}
                      </div>
                    )}
                    {d.rationale && <p className="text-white/50 text-xs font-light mt-2 leading-relaxed">{d.rationale}</p>}
                  </div>
                  {isStaff && (
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <button onClick={() => toggleLearn(d)} data-testid={`decision-learn-${d.id}`}
                        className={`text-[0.55rem] font-mono uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${d.learn_enabled === false ? "border-white/20 text-white/40" : "border-emerald-400/40 text-emerald-300"}`}>
                        {d.learn_enabled === false ? "AI off" : "AI on"}
                      </button>
                      <button onClick={() => remove(d.id)} data-testid={`decision-delete-${d.id}`} className="text-white/30 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
